import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import "./SupplierTopbar.css";

import supplierPortalService from "../../services/supplierPortalService";

const SupplierTopbar = ({
  supplier,
  sidebarOpen = false,
  onMenuClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);

  const [profileMenuOpen, setProfileMenuOpen] =
    useState(false);

  const [profilePhotoError, setProfilePhotoError] =
    useState(false);

  const [profileImageOpen, setProfileImageOpen] =
    useState(false);

  const [profileData, setProfileData] =
    useState(() => {
      const stored =
        supplierPortalService.getStoredSupplier();

      return {
        ...(stored || {}),
        ...(supplier || {}),
      };
    });

  useEffect(() => {
    if (supplier) {
      setProfileData((previous) => ({
        ...previous,
        ...supplier,
      }));
    }
  }, [supplier]);

  useEffect(() => {
    let active = true;

    const loadSupplierProfile = async () => {
      if (!supplierPortalService.isLoggedIn()) {
        return;
      }

      try {
        const profile =
          await supplierPortalService.getMe();

        if (active && profile) {
          setProfileData(profile);
        }
      } catch (error) {
        console.error(
          "Supplier profile loading failed:",
          error
        );
      }
    };

    loadSupplierProfile();

    return () => {
      active = false;
    };
  }, []);

  const rawSupplierPhoto =
    profileData?.profile_photo ||
    profileData?.profile_photo_url ||
    profileData?.photo_url ||
    profileData?.photo ||
    profileData?.image_url ||
    profileData?.image ||
    "";

  const supplierPhoto =
    supplierPortalService.resolveFileUrl(
      rawSupplierPhoto
    );

  const supplierName =
    profileData?.supplier_name ||
    profileData?.name ||
    "Supplier";

  const supplierMobile =
    profileData?.mobile || "";

  const supplierCode =
    profileData?.supplier_code || "";

  const supplierEmail =
    profileData?.email || "";

  const supplierInitial =
    supplierName?.charAt(0)?.toUpperCase() ||
    "S";

  useEffect(() => {
    setProfilePhotoError(false);
  }, [supplierPhoto]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(
          event.target
        )
      ) {
        setProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setProfileMenuOpen(false);
        setProfileImageOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  useEffect(() => {
    if (profileImageOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [profileImageOpen]);

  const openProfileImage = (event) => {
    event.stopPropagation();

    if (!supplierPhoto) {
      return;
    }

    setProfileImageOpen(true);
  };

  const closeProfileImage = () => {
    setProfileImageOpen(false);
  };

  const getPageTitle = () => {
    const pathname = location.pathname;

    if (
      pathname.includes(
        "/supplier/vehicle-documents"
      )
    ) {
      return "Vehicle Documents";
    }

    if (
      pathname.includes(
        "/supplier/statements"
      )
    ) {
      return "Statements";
    }

    if (
      pathname === "/supplier/dashboard" ||
      pathname === "/supplier"
    ) {
      return "Dashboard";
    }

    return "Dashboard";
  };

  const pageTitle = getPageTitle();

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) {
      return;
    }

    setProfileMenuOpen(false);
    setProfileImageOpen(false);

    supplierPortalService.logout();

    navigate("/supplier/login", {
      replace: true,
    });
  };

  const showPhoto =
    Boolean(supplierPhoto) &&
    !profilePhotoError;

  return (
    <>
      <header className="supplier-topbar">
        <div className="supplier-topbar-left">
          <button
            type="button"
            className="supplier-menu-btn"
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            onClick={onMenuClick}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="supplier-page-title">
            {pageTitle}
          </div>
        </div>

        <div className="supplier-topbar-right">
          <div
            className="supplier-profile-menu"
            ref={profileMenuRef}
          >
            <button
              type="button"
              className={`supplier-user-trigger ${
                profileMenuOpen ? "open" : ""
              }`}
              aria-label="Open supplier profile"
              aria-expanded={profileMenuOpen}
              onClick={() =>
                setProfileMenuOpen(
                  (value) => !value
                )
              }
            >
              {showPhoto ? (
                <img
                  src={supplierPhoto}
                  alt={supplierName}
                  className="supplier-user-photo"
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";

                    setProfilePhotoError(true);
                  }}
                />
              ) : (
                <span className="supplier-user-fallback">
                  {supplierInitial}
                </span>
              )}

              <span className="supplier-user-chevron">
                {profileMenuOpen ? "⌃" : "⌄"}
              </span>
            </button>

            {profileMenuOpen && (
              <div className="supplier-profile-popover">
                <div className="supplier-popover-head">
                  <div
                    className={
                      showPhoto
                        ? "supplier-popover-avatar supplier-popover-avatar-clickable"
                        : "supplier-popover-avatar"
                    }
                    onClick={
                      showPhoto
                        ? openProfileImage
                        : undefined
                    }
                  >
                    {showPhoto ? (
                      <img
                        src={supplierPhoto}
                        alt={supplierName}
                        onError={(event) => {
                          event.currentTarget.style.display =
                            "none";

                          setProfilePhotoError(true);
                        }}
                      />
                    ) : (
                      <span>
                        {supplierInitial}
                      </span>
                    )}
                  </div>

                  <div className="supplier-popover-identity">
                    <strong>
                      {supplierName}
                    </strong>

                    <span>
                      Supplier Account
                    </span>
                  </div>
                </div>

                <div className="supplier-popover-divider"></div>

                <div className="supplier-popover-details">
                  <div className="supplier-popover-detail">
                    <span>
                      Mobile
                    </span>

                    <strong>
                      {supplierMobile ||
                        "Not available"}
                    </strong>
                  </div>

                  <div className="supplier-popover-detail">
                    <span>
                      Supplier Code
                    </span>

                    <strong>
                      {supplierCode ||
                        "Not available"}
                    </strong>
                  </div>

                  {supplierEmail && (
                    <div className="supplier-popover-detail">
                      <span>
                        Email
                      </span>

                      <strong>
                        {supplierEmail}
                      </strong>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="supplier-profile-logout"
                  onClick={handleLogout}
                >
                  <span>
                    ↪
                  </span>

                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {profileImageOpen &&
        showPhoto && (
          <div
            className="supplier-image-preview-overlay"
            onClick={closeProfileImage}
          >
            <button
              type="button"
              className="supplier-image-preview-close"
              aria-label="Close image"
              onClick={closeProfileImage}
            >
              ×
            </button>

            <div
              className="supplier-image-preview-container"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <img
                src={supplierPhoto}
                alt={supplierName}
                className="supplier-image-preview"
              />
            </div>
          </div>
        )}
    </>
  );
};

export default SupplierTopbar;